var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
import * as Long from "long";
import { selectBalancingBuff, selectBalancingBuffFactorAddition, } from "../balancingSelectors/selectBalancingBuffs";
import { selectBalancingBuilding } from "../balancingSelectors/selectBalancingBuildings";
import { selectBalancingSkipProductionFactor } from "../balancingSelectors/selectBalancingConstants";
import { selectBalancingRecipe } from "../balancingSelectors/selectBalancingRecipes";
import { selectBalancingResource, selectResourceInfo, } from "../balancingSelectors/selectBalancingResources";
import { IdPersistence } from "../constants/IdPersistence";
import { IdResource } from "../constants/IdResource";
import { TypeRecipeCost } from "../features/balancing/TypeRecipeCost";
import { TypeResource } from "../features/balancing/TypeResource";
import { utilBuff } from "../utils/utilBuff";
import { utilBuildings } from "../utils/utilBuildings";
import { utilNumber } from "../utils/utilNumber";
import { utilPersistence } from "../utils/utilPersistence";
import { utilSkipProcess } from "../utils/utilSkipProcess";
import { utilTime } from "../utils/utilTime";
import { selectProductionResourceBuffAmount } from "./selectBuffsInfo";
import { selectBuildingInfoById, selectSelectedBuildingInfo } from "./selectExistingBuildingInfo";
import { selectHappinessAmount } from "./selectHappinessInfo";
import { selectNotEnoughResourcesMissingResources } from "./selectNotEnoughResources";
import { selectIsStorageFullForProduct } from "./selectStorageInfo";
var sortRecipePrio = function (a, b) {
    return (a.recipe.sortOrder != undefined ? a.recipe.sortOrder : 999) -
        (b.recipe.sortOrder != undefined ? b.recipe.sortOrder : 999);
};
export var selectAllRecipeInfo = function (state, building) { return getRecipeInfo(state, building, getRecipeList); };
export var selectAllBoostRecipeInfo = function (state, building) { return getRecipeInfo(state, building, getBoostRecipeList); };
export var selectRecipeInfo = function (state, recipeId) {
    if (recipeId == undefined) {
        return undefined;
    }
    return mapRecipeIdToRecipeData(state, recipeId);
};
export var selectRecipeFactorAddition = function (recipe) {
    var buff = recipe && recipe.recipe.buffId ? selectBalancingBuff(recipe.recipe.buffId) : undefined;
    return buff && buff.factorAddition ? buff.factorAddition : 0;
};
export var selectAllRecipeInfoByBuildingId = function (state, buildingId) {
    return selectAllRecipeInfo(state, selectBuildingInfoById(state, buildingId));
};
export var selectAllRecipesForAllStages = function (state, buildingTypeId) {
    var building = selectBalancingBuilding(buildingTypeId);
    if (building == undefined) {
        throw new Error("Building with typeId: " + buildingTypeId + " not found in Balancing.");
    }
    return building.stage.map(function (stage) { return getRecipeInfoFromBuildingStage(state, stage, getRecipeList); });
};
export var selectNonCollectibleProduct = function (state, products, building) {
    var nonCollectibleProduct = products &&
        products.find(function (value) {
            var resourceInfo = selectResourceInfo(value.resourceId);
            var boostedAmount = selectRecipeBoostedAmount(state, value.amount, building);
            var resourceBuffAmount = selectProductionResourceBuffAmount(state, value.resourceId, value.amount);
            var resourceSumAmount = Long.fromValue(value.amount)
                .add(boostedAmount)
                .add(resourceBuffAmount);
            return (checkNonCollectibleProductType(resourceInfo.type) &&
                selectIsStorageFullForProduct(state, value.resourceId, resourceSumAmount));
        });
    if (nonCollectibleProduct != undefined) {
        return nonCollectibleProduct;
    }
    return undefined;
};
export var selectSelectedBuildingNonCollectibleProduct = function (state, products) {
    var selectedBuilding = selectSelectedBuildingInfo(state);
    return selectNonCollectibleProduct(state, products, selectedBuilding);
};
var getRecipeInfo = function (state, building, getRecipes) {
    if (building == undefined || building.balancing == undefined) {
        return undefined;
    }
    var stageIdx = utilBuildings.getPreviousProtoStage(building);
    var buildingStage = building.balancing.stage[stageIdx];
    return getRecipeInfoFromBuildingStage(state, buildingStage, getRecipes);
};
var getRecipeInfoFromBuildingStage = function (state, stage, getRecipes) {
    var recipes = getRecipes(stage);
    if (recipes == undefined) {
        return undefined;
    }
    return recipes
        .split(",")
        .map(function (val) { return mapRecipeIdToRecipeData(state, parseInt(val, 10)); })
        .sort(sortRecipePrio);
};
var getRecipeList = function (stage) { return stage.recipeIDs; };
var getBoostRecipeList = function (stage) { return stage.boostRecipeIDs; };
export var mapRecipeIdToRecipeData = function (state, id) {
    var recipe = selectBalancingRecipe(id);
    var cost = calculateCosts(state, recipe);
    var product = getProducts(state, recipe);
    return {
        cost: cost,
        product: product,
        recipe: recipe,
    };
};
var checkNonCollectibleProductType = function (resourceType) {
    return (resourceType === TypeResource.ContractsResources ||
        resourceType === TypeResource.Water ||
        resourceType === TypeResource.CafeResources);
};
export var calculateCosts = function (state, recipe) {
    if (!recipe.cost) {
        return [];
    }
    var costMap = recipe.cost.reduce(function (acc, cost) {
        var existing = acc.get(cost.resourceId);
        if (existing) {
            var newCost = __assign({}, existing, { amount: existing.amount.add(Long.fromValue(applyRecipeCostBuffs(state, cost))) });
            acc.set(cost.resourceId, newCost);
            return acc;
        }
        var resource = selectBalancingResource(cost.resourceId);
        var amount = applyRecipeCostBuffs(state, cost);
        acc.set(cost.resourceId, __assign({}, resource, { amount: Long.fromInt(amount), ownedAmount: getOwnedResourceCount(state, cost.resourceId), type: cost.type }));
        return acc;
    }, new Map());
    return Array.from(costMap).map(function (val) { return val[1]; });
};
export var getProductionCostBuff = function (happiness) {
    var buffAmount;
    if (happiness >= 0) {
        buffAmount = 1 / (happiness * 0.017 + 1);
    }
    else {
        buffAmount = 10.5 / (happiness * 0.014 - 2.1) + 6;
    }
    return buffAmount;
};
export var getProductionCostBuffAsPercentage = function (happiness) {
    return Math.round(getProductionCostBuff(happiness) * 100) - 100;
};
var applyRecipeCostBuffs = function (state, cost) {
    var happiness = selectHappinessAmount(state);
    // TODO: add handling to other recipeCost buffs
    return cost.type === TypeRecipeCost.ProductionCost
        ? utilBuff.buffAdditionValue(cost.amount, getProductionCostBuff(happiness))
        : cost.amount;
};
var getProducts = function (state, recipe) {
    if (recipe.product == undefined) {
        return [];
    }
    return recipe.product.map(function (val) {
        var resource = selectBalancingResource(val.resourceId);
        return __assign({}, resource, { amount: Long.fromInt(val.amount) });
    });
};
var getOwnedResourceCount = function (state, resourceId) {
    var resource = state.inventoryInfo.resource.find(function (val) { return val.typeId === resourceId; });
    return resource ? resource.amount || Long.ZERO : Long.ZERO;
};
export var selectLastRecipeId = function (buildingTypeId) {
    var lastProduced = utilPersistence.getValue(IdPersistence.LastProduction, {});
    var productionData = lastProduced[buildingTypeId];
    if (productionData == undefined || productionData.recipeId == undefined) {
        return 0;
    }
    return productionData.recipeId;
};
export var selectFreeRecipeResourceBuildingId = function (state, resourceId, building) {
    var stage = building.balancing.stage[0];
    var recipes = stage.recipeIDs
        ? stage.recipeIDs.split(",").map(function (entry) { return parseInt(entry, 10); })
        : [];
    var recipeId = recipes.find(function (recipe) {
        var foundRecipe = selectBalancingRecipe(recipe);
        if (recipe === undefined) {
            return false;
        }
        var costs = foundRecipe.cost;
        if (costs === undefined || costs === []) {
            return false;
        }
        var isNotPremium = costs.find(function (cost) { return cost.resourceId === IdResource.Money; });
        var hasResource = costs.find(function (cost) { return cost.resourceId === resourceId; });
        return isNotPremium !== undefined && hasResource !== undefined;
    });
    return recipeId || 0;
};
export var selectSelectedBuildingRecipeBoostedAmount = function (state, resourceAmount) {
    var selectedBuilding = state.playerBase.buildings.building;
    return selectRecipeBoostedAmount(state, resourceAmount, selectedBuilding);
};
export var selectRecipeBoostedAmount = function (state, resourceAmount, building) {
    var boostAmount = 0;
    if (building && building.production && building.production.production) {
        var boosterId = building.production.production.boosterId;
        if (boosterId) {
            var boosterRecipe = selectRecipeInfo(state, boosterId);
            boostAmount = selectBalancingBuffFactorAddition(boosterRecipe.recipe.buffId);
        }
    }
    return boostAmount === 0
        ? Long.ZERO
        : utilNumber.multiplyRounding(resourceAmount, boostAmount).sub(resourceAmount);
};
export var selectMissingRecipeCost = function (state, recipeId, amount) {
    var recipe = selectBalancingRecipe(recipeId);
    var recipeCostData = calculateCosts(state, recipe);
    var recipeCost = recipeCostData.map(function (costs) { return ({
        amount: costs.amount.multiply(amount),
        typeId: costs.id,
    }); });
    return selectNotEnoughResourcesMissingResources(state, recipeCost);
};
export var selectRecipeProductionSkipCost = function (state, building) {
    var remainingTime = utilTime.secondsLeftTill(building.production.production.remainingTime);
    var factor = selectBalancingSkipProductionFactor();
    return Long.fromInt(utilSkipProcess.calculateCostToSkipProcess(remainingTime, factor));
};



//////////////////
// WEBPACK FOOTER
// ./src/selectors/selectRecipeInfo.ts
// module id = 72
// module chunks = 3